import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Plus, ChevronRight, Printer } from 'lucide-react';
import { ticketingApi, type Asset } from '@/lib/ticketing-api';
import { PageHeader, DataTable, type Column } from '@/components/ui/DataTable';
import { StatusPill, KpiStat } from '@/components/ui/primitives';
import { Badge } from '@/components/ui/Badge';
import { QuickFormModal } from '@/components/QuickFormModal';

// §26 asset lifecycle: procurement → installation → operation → retirement.
const LIFECYCLE_STEPS = ['Requested', 'Ordered', 'Delivered', 'Installed', 'Active', 'Maintenance', 'Retired'] as const;

// Controlled asset-category taxonomy (mirrors ASSET_CATEGORIES in the schema).
const ASSET_CATEGORIES = ['Camera', 'NVR/DVR', 'VMS Server', 'Access Controller', 'Alarm Panel', 'Network Switch', 'Storage', 'UPS', 'Sensor', 'Other'];

// Map a lifecycle stage to a StatusPill `status` keyword that resolves to the
// intended tone via statusTone (Active→ok, Maintenance→warning, Retired→muted,
// the in-flight procurement stages → info/primary via the 'on_hold' family fallback).
function lifecycleStatus(stage: string): string {
  switch (stage) {
    case 'Active': return 'active';
    case 'Maintenance': return 'warning';
    case 'Retired': return 'closed';
    default: return 'in_progress'; // Requested / Ordered / Delivered / Installed
  }
}

export function AssetsPage() {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { data: assets, isLoading } = useQuery({ queryKey: ['assets'], queryFn: ticketingApi.listAssets });
  const { data: systemTypes } = useQuery({ queryKey: ['system-types'], queryFn: ticketingApi.listSystemTypes });
  const { data: sites } = useQuery({ queryKey: ['sites'], queryFn: ticketingApi.listSites });

  // Resolve an asset's site UUID → human name (the API returns siteId, not a name).
  const siteName = (a: Asset) => sites?.find((s) => s.id === a.siteId)?.name;

  const stageOf = (a: Asset) =>
    a.lifecycle ?? (a.status === 'in_maintenance' ? 'Maintenance' : a.status === 'active' ? 'Active' : 'Active');

  const active = (assets ?? []).filter((a) => stageOf(a) === 'Active').length;
  const maint = (assets ?? []).filter((a) => stageOf(a) === 'Maintenance').length;
  const value = (assets ?? []).reduce((s, a) => s + (a.purchasePrice ?? 0), 0);

  const columns: Column<Asset>[] = [
    { key: 'assetId', header: t('assets.columns.assetId'), className: 'tk-num font-medium' },
    { key: 'name', header: t('assets.columns.name') },
    {
      key: 'category', header: t('assets.columns.category'),
      render: (a) => <Badge>{a.category}</Badge>,
    },
    {
      key: 'lifecycle', header: t('assets.columns.lifecycle'),
      render: (a) => {
        const stage = stageOf(a);
        return <StatusPill status={lifecycleStatus(stage)} label={stage} />;
      },
    },
    { key: 'manufacturer', header: t('assets.columns.makeModel'), render: (a) => <span className="text-muted">{[a.manufacturer, a.model].filter(Boolean).join(' ') || '—'}</span> },
    { key: 'serialNumber', header: t('assets.columns.serial'), className: 'tk-num', render: (a) => <span className="text-muted">{a.serialNumber ?? '—'}</span> },
    { key: 'siteName', header: t('assets.columns.site'), render: (a) => <span className="text-muted">{siteName(a) ?? '—'}</span> },
    { key: 'vendor', header: t('assets.columns.vendor'), render: (a) => <span className="text-muted">{a.vendor ?? '—'}</span> },
    { key: 'contract', header: t('assets.columns.contract'), className: 'tk-num', render: (a) => <span className="text-muted">{a.contract ?? '—'}</span> },
    { key: 'ticketHistory', header: t('assets.columns.tickets'), align: 'right', className: 'tk-num', render: (a) => <span className="text-muted">{a.ticketHistory ?? 0}</span> },
    {
      key: 'warrantyEnd', header: t('assets.columns.warranty'), align: 'right',
      render: (a) => <span className="tk-num text-muted">{a.warrantyEnd ? new Date(a.warrantyEnd).toLocaleDateString() : '—'}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('assets.title')}
        subtitle={t('assets.subtitle')}
        action={<button className="tk-btn-primary" onClick={() => setAddOpen(true)}><Plus size={16} /> {t('assets.actions.add')}</button>}
      />

      {addOpen && (
        <QuickFormModal
          title={t('assets.form.title')}
          submitLabel={t('assets.actions.add')}
          fields={[
            { key: 'name', label: t('assets.form.name'), required: true, placeholder: t('assets.form.namePlaceholder') },
            { key: 'category', label: t('assets.form.category'), type: 'select', required: true,
              options: ASSET_CATEGORIES.map((c) => ({ value: c, label: c })) },
            { key: 'systemTypeId', label: t('assets.form.systemType'), type: 'select',
              options: [{ value: '', label: '—' }, ...(systemTypes ?? []).map((s) => ({ value: s.code, label: s.name }))] },
            { key: 'siteId', label: t('assets.form.site'), type: 'select', required: true,
              options: [{ value: '', label: t('assets.form.selectSite') }, ...(sites ?? []).map((s) => ({ value: s.id, label: s.name }))] },
            { key: 'manufacturer', label: t('assets.form.manufacturer'), placeholder: 'Dahua' },
            { key: 'model', label: t('assets.form.model'), placeholder: 'NVR5216' },
            { key: 'serialNumber', label: t('assets.form.serialNumber') },
          ]}
          onClose={() => setAddOpen(false)}
          onSubmit={async (v) => {
            await ticketingApi.createAsset({
              name: v.name, category: v.category,
              systemTypeId: v.systemTypeId || undefined, siteId: v.siteId || undefined,
              manufacturer: v.manufacturer || undefined, model: v.model || undefined, serialNumber: v.serialNumber || undefined,
            });
            queryClient.invalidateQueries({ queryKey: ['assets'] });
          }}
        />
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiStat label={t('assets.kpi.total')} value={assets?.length ?? 0} tone="info" />
        <KpiStat label={t('assets.kpi.active')} value={active} tone="ok" />
        <KpiStat label={t('assets.kpi.inMaintenance')} value={maint} tone={maint ? 'warning' : 'ok'} />
        <KpiStat label={t('assets.kpi.value')} value={`$${value.toLocaleString()}`} tone="info" />
      </div>

      {/* Lifecycle stepper legend (§26) */}
      <div className="tk-card">
        <div className="mb-3 text-sm font-medium">{t('assets.lifecycle.heading')}</div>
        <div className="flex flex-wrap items-center gap-1.5">
          {LIFECYCLE_STEPS.map((step, i) => (
            <div key={step} className="flex items-center gap-1.5">
              <StatusPill status={lifecycleStatus(step)} label={step} />
              {i < LIFECYCLE_STEPS.length - 1 && <ChevronRight size={14} className="text-muted" />}
            </div>
          ))}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={assets}
        loading={isLoading}
        emptyIcon={Boxes}
        emptyTitle={t('assets.empty.title')}
        emptyHint={t('assets.empty.hint')}
        onRowClick={(a) => navigate(`/assets/${a.id}`)}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        bulkActions={(ids) => (
          <button
            className="tk-btn-primary px-3 py-1 text-xs"
            // URL-length guard: a full sheet is 24 labels; cap well below any limit.
            onClick={() => navigate(`/assets/labels?ids=${[...ids].slice(0, 100).join(',')}`)}
          >
            <Printer size={14} /> {t('assets.actions.printLabels', { count: ids.size })}
          </button>
        )}
      />
    </div>
  );
}
